import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Menu,
  Message,
  Segment,
  Statistic,
  Table,
} from 'semantic-ui-react';

import { API, showError, showSuccess } from '../helpers';
import BillingPriceImportV82 from './BillingPriceImportV82';
import BillingProfitabilityV82 from '../pages/Billing/BillingProfitabilityV82';
import './InternalBillingSettingV817.css';

const percentFromBasisPoints = (value) => (Number(value || 0) / 100).toFixed(2);

const basisPointsFromPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return null;
  return Math.round(parsed * 100);
};

const formatCount = (value) => {
  try {
    return window.BigInt(String(value || 0)).toLocaleString();
  } catch {
    return String(value || 0);
  }
};

const formatProviderCost = (value, available, complete) =>
  available && complete ? '$' + (value || '0') : '-';

const formatDateTime = (timestamp) =>
  timestamp ? new Date(Number(timestamp) * 1000).toLocaleString() : '-';

export const isDefaultCustomerPriceValueV817 = (value) => {
  const normalized = String(value ?? '').trim();
  if (!/^(?:\d+|\d*\.\d{1,9})$/.test(normalized)) return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0;
};

const DefaultCustomerPriceV817 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({
    input_price_usd_per_million: '5.00',
    cache_creation_price_usd_per_million: '6.00',
    cached_input_price_usd_per_million: '0.50',
    output_price_usd_per_million: '20.00',
    note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/internal/default-customer-price'
      );
      if (!response?.data?.success) throw new Error(response?.data?.message);
      const price = response.data.data;
      setCurrent(price);
      setForm({
        input_price_usd_per_million:
          price.input_price_usd_per_million || '5.00',
        cache_creation_price_usd_per_million:
          price.cache_creation_price_usd_per_million || '6.00',
        cached_input_price_usd_per_million:
          price.cached_input_price_usd_per_million || '0.50',
        output_price_usd_per_million:
          price.output_price_usd_per_million || '20.00',
        note: '',
      });
    } catch (error) {
      showError(
        error.message || t('setting.internal_billing.errors.default_price_load')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (field, value) =>
    setForm((currentForm) => ({ ...currentForm, [field]: value }));

  const save = async () => {
    const priceFields = [
      'input_price_usd_per_million',
      'cache_creation_price_usd_per_million',
      'cached_input_price_usd_per_million',
      'output_price_usd_per_million',
    ];
    if (
      !priceFields.every((field) =>
        isDefaultCustomerPriceValueV817(form[field])
      )
    ) {
      showError(t('setting.internal_billing.errors.default_price_invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await API.put(
        '/api/billing/v8/internal/default-customer-price',
        form
      );
      if (!response?.data?.success) throw new Error(response?.data?.message);
      const price = response.data.data;
      setCurrent(price);
      setForm((currentForm) => ({
        ...currentForm,
        input_price_usd_per_million: price.input_price_usd_per_million,
        cache_creation_price_usd_per_million:
          price.cache_creation_price_usd_per_million,
        cached_input_price_usd_per_million:
          price.cached_input_price_usd_per_million,
        output_price_usd_per_million: price.output_price_usd_per_million,
        note: '',
      }));
      showSuccess(t('setting.internal_billing.messages.default_price_saved'));
    } catch (error) {
      showError(
        error.message || t('setting.internal_billing.errors.default_price_save')
      );
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    ['input_price_usd_per_million', 'input'],
    ['cache_creation_price_usd_per_million', 'cache_creation'],
    ['cached_input_price_usd_per_million', 'cached_input'],
    ['output_price_usd_per_million', 'output'],
  ];

  return (
    <Form loading={loading} className='internal-default-price-v817'>
      <div className='internal-section-heading-v817'>
        <div>
          <Header as='h3'>
            {t('setting.internal_billing.default_price.title')}
          </Header>
          <span>
            {t('setting.internal_billing.default_price.version', {
              version: current?.version || 0,
            })}
          </span>
        </div>
        {current?.system_default ? (
          <Label basic>
            {t('setting.internal_billing.default_price.system_default')}
          </Label>
        ) : null}
      </div>

      <Message
        info
        content={t('setting.internal_billing.default_price.scope')}
      />
      <div className='internal-default-price-grid-v817'>
        {fields.map(([field, label]) => (
          <Form.Input
            key={field}
            label={t('setting.internal_billing.default_price.' + label)}
            type='number'
            min='0'
            step='0.000000001'
            value={form[field]}
            onChange={(_, { value }) => updateField(field, value)}
          />
        ))}
      </div>
      <Form.Input
        label={t('setting.internal_billing.default_price.note')}
        value={form.note}
        onChange={(_, { value }) => updateField('note', value)}
      />
      <Button primary type='button' icon labelPosition='left' onClick={save}>
        <Icon name='save' />
        {t('setting.internal_billing.default_price.save')}
      </Button>
    </Form>
  );
};
const CostPolicyV817 = ({ onSaved }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({
    vertex_base_cost_profile_key: '',
    flex_percent: '50.00',
    express_percent: '100.00',
    agent_discount_percent: '0.00',
    billing_day: 1,
    note: '',
  });
  const [current, setCurrent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policyResponse, profileResponse] = await Promise.all([
        API.get('/api/billing/v8/internal/cost-policy'),
        API.get('/api/billing/v8/admin/provider-cost-profiles'),
      ]);
      if (!policyResponse?.data?.success)
        throw new Error(policyResponse?.data?.message);
      if (!profileResponse?.data?.success)
        throw new Error(profileResponse?.data?.message);
      const policy = policyResponse.data.data;
      setCurrent(policy);
      setProfiles(profileResponse.data.data || []);
      setForm({
        vertex_base_cost_profile_key: policy.vertex_base_cost_profile_key || '',
        flex_percent: percentFromBasisPoints(
          policy.vertex_flex_multiplier_basis_points
        ),
        express_percent: percentFromBasisPoints(
          policy.vertex_express_multiplier_basis_points
        ),
        agent_discount_percent: percentFromBasisPoints(
          policy.official_agent_discount_basis_points
        ),
        billing_day: policy.billing_day || 1,
        note: '',
      });
    } catch (error) {
      showError(error.message || t('setting.internal_billing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const profileOptions = useMemo(
    () =>
      profiles.map((profile) => ({
        key: profile.id,
        value: profile.profile_key,
        text:
          profile.profile_key +
          ' · ' +
          profile.provider +
          ' / ' +
          (profile.region || '-'),
      })),
    [profiles]
  );

  const save = async () => {
    const flexBasisPoints = basisPointsFromPercent(form.flex_percent);
    const expressBasisPoints = basisPointsFromPercent(form.express_percent);
    const discountBasisPoints = basisPointsFromPercent(
      form.agent_discount_percent
    );
    const billingDay = Number(form.billing_day);
    if (
      flexBasisPoints === null ||
      expressBasisPoints === null ||
      discountBasisPoints === null ||
      !Number.isInteger(billingDay) ||
      billingDay < 1 ||
      billingDay > 31
    ) {
      showError(t('setting.internal_billing.errors.invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await API.put('/api/billing/v8/internal/cost-policy', {
        vertex_base_cost_profile_key: form.vertex_base_cost_profile_key || '',
        vertex_flex_multiplier_basis_points: flexBasisPoints,
        vertex_express_multiplier_basis_points: expressBasisPoints,
        official_agent_discount_basis_points: discountBasisPoints,
        billing_day: billingDay,
        note: form.note,
      });
      if (!response?.data?.success) throw new Error(response?.data?.message);
      setCurrent(response.data.data);
      setForm((value) => ({ ...value, note: '' }));
      showSuccess(t('setting.internal_billing.messages.saved'));
      onSaved();
    } catch (error) {
      showError(error.message || t('setting.internal_billing.errors.save'));
    } finally {
      setLoading(false);
    }
  };

  const standardPayable = Math.max(
    0,
    100 - Number(form.agent_discount_percent || 0)
  );
  const flexPayable = (Number(form.flex_percent || 0) / 100) * standardPayable;
  const expressPayable =
    (Number(form.express_percent || 0) / 100) * standardPayable;

  return (
    <Form loading={loading} className='internal-policy-v817'>
      <div className='internal-section-heading-v817'>
        <div>
          <Header as='h3'>{t('setting.internal_billing.policy.title')}</Header>
          <span>
            {t('setting.internal_billing.policy.version', {
              version: current?.version || 0,
            })}
          </span>
        </div>
        {current?.default_policy ? (
          <Label basic>
            {t('setting.internal_billing.policy.default_policy')}
          </Label>
        ) : null}
      </div>

      <Form.Group widths='equal'>
        <Form.Field>
          <label>
            {t('setting.internal_billing.policy.base_cost_profile')}
          </label>
          <Dropdown
            clearable
            fluid
            search
            selection
            loading={loading}
            options={profileOptions}
            value={form.vertex_base_cost_profile_key}
            onChange={(_, { value }) =>
              setForm((currentForm) => ({
                ...currentForm,
                vertex_base_cost_profile_key: value || '',
              }))
            }
          />
        </Form.Field>
        <Form.Input
          label={t('setting.internal_billing.policy.flex_multiplier')}
          type='number'
          min='0'
          max='100'
          step='0.01'
          value={form.flex_percent}
          onChange={(_, { value }) =>
            setForm((currentForm) => ({
              ...currentForm,
              flex_percent: value,
            }))
          }
        />
        <Form.Input
          label={t('setting.internal_billing.policy.express_multiplier')}
          type='number'
          min='0'
          max='100'
          step='0.01'
          value={form.express_percent}
          onChange={(_, { value }) =>
            setForm((currentForm) => ({
              ...currentForm,
              express_percent: value,
            }))
          }
        />
        <Form.Input
          label={t('setting.internal_billing.policy.agent_discount')}
          type='number'
          min='0'
          max='100'
          step='0.01'
          value={form.agent_discount_percent}
          onChange={(_, { value }) =>
            setForm((currentForm) => ({
              ...currentForm,
              agent_discount_percent: value,
            }))
          }
        />
        <Form.Input
          label={t('setting.internal_billing.policy.billing_day')}
          type='number'
          min='1'
          max='31'
          step='1'
          value={form.billing_day}
          onChange={(_, { value }) =>
            setForm((currentForm) => ({
              ...currentForm,
              billing_day: value,
            }))
          }
        />
      </Form.Group>

      <div className='internal-formula-band-v817'>
        <span>
          <strong>Standard</strong>
          {t('setting.internal_billing.policy.effective_payable', {
            percent: standardPayable.toFixed(2),
          })}
        </span>
        <span>
          <strong>Flex</strong>
          {t('setting.internal_billing.policy.effective_payable', {
            percent: flexPayable.toFixed(2),
          })}
        </span>
        <span>
          <strong>Express</strong>
          {t('setting.internal_billing.policy.effective_payable', {
            percent: expressPayable.toFixed(2),
          })}
        </span>
      </div>

      <Form.Input
        label={t('setting.internal_billing.policy.note')}
        value={form.note}
        onChange={(_, { value }) =>
          setForm((currentForm) => ({ ...currentForm, note: value }))
        }
      />
      <Button primary type='button' icon labelPosition='left' onClick={save}>
        <Icon name='save' />
        {t('setting.internal_billing.policy.save')}
      </Button>
    </Form>
  );
};

const usageMeterKeysV817 = [
  'input_units',
  'input_text_tokens',
  'input_image_tokens',
  'input_audio_tokens',
  'input_video_tokens',
  'input_document_tokens',
  'cached_input_tokens',
  'cache_creation_tokens',
  'output_units',
  'output_text_tokens',
  'thinking_tokens',
  'image_output_tokens',
  'image_output_count',
  'audio_character_units',
  'audio_duration_millis',
  'video_units',
  'video_duration_millis',
];

const ProviderUsageV817 = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/billing/v8/internal/provider-usage');
      if (!response?.data?.success) throw new Error(response?.data?.message);
      setReport(response.data.data);
    } catch (error) {
      showError(error.message || t('setting.internal_billing.errors.usage'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading && !report) return <Loader active inline='centered' />;
  const rows = report?.rows || [];
  const totals = report?.totals || {};

  return (
    <section>
      <div className='internal-section-heading-v817'>
        <div>
          <Header as='h3'>{t('setting.internal_billing.usage.title')}</Header>
          <span>
            {formatDateTime(report?.period_start)} -{' '}
            {formatDateTime(report?.period_end)}
          </span>
          <span className='internal-usage-scope-v817'>
            {t('setting.internal_billing.usage.all_accounts_scope')}
          </span>
        </div>
        <Button
          basic
          icon='refresh'
          loading={loading}
          title={t('setting.internal_billing.usage.refresh')}
          aria-label={t('setting.internal_billing.usage.refresh')}
          onClick={load}
        />
      </div>

      <Statistic.Group
        size='small'
        widths='four'
        className='internal-stats-v817'
      >
        <Statistic
          label={t('setting.internal_billing.usage.providers')}
          value={formatCount(totals.provider_count)}
        />
        <Statistic
          label={t('setting.internal_billing.usage.attempts')}
          value={formatCount(totals.attempt_count)}
        />
        <Statistic
          label={t('setting.internal_billing.meters.input_units')}
          value={formatCount(totals.usage?.input_units)}
        />
        <Statistic
          label={t('setting.internal_billing.meters.output_units')}
          value={formatCount(totals.usage?.output_units)}
        />
        <Statistic
          label={t('setting.internal_billing.meters.cached_input_tokens')}
          value={formatCount(totals.usage?.cached_input_tokens)}
        />
        <Statistic
          label={t('setting.internal_billing.meters.thinking_tokens')}
          value={formatCount(totals.usage?.thinking_tokens)}
        />
        <Statistic
          label={t('setting.internal_billing.usage.original_cost')}
          value={formatProviderCost(
            totals.provider_original_cost_usd,
            totals.provider_cost_available,
            totals.provider_cost_complete
          )}
          title={
            totals.provider_cost_available && totals.provider_cost_complete
              ? undefined
              : t('setting.internal_billing.usage.cost_unavailable')
          }
        />
        <Statistic
          label={t('setting.internal_billing.usage.actual_cost')}
          value={formatProviderCost(
            totals.provider_actual_cost_usd,
            totals.provider_cost_available,
            totals.provider_cost_complete
          )}
          title={
            totals.provider_cost_available && totals.provider_cost_complete
              ? undefined
              : t('setting.internal_billing.usage.cost_unavailable')
          }
        />
      </Statistic.Group>

      {rows.length === 0 ? (
        <Message info content={t('setting.internal_billing.usage.empty')} />
      ) : (
        <div className='internal-table-scroll-v817'>
          <Table compact celled selectable unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing />
                <Table.HeaderCell>
                  {t('setting.internal_billing.usage.provider')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.internal_billing.usage.class')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.usage.requests')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.meters.input_units')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.meters.cached_input_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.meters.output_units')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.usage.original_cost')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.usage.actual_cost')}
                </Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  {t('setting.internal_billing.usage.effective')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => {
                const key =
                  String(row.channel_id) + ':' + row.provider_billing_class;
                const open = Boolean(expanded[key]);
                return (
                  <Fragment key={key}>
                    <Table.Row>
                      <Table.Cell collapsing>
                        <Button
                          basic
                          compact
                          icon={open ? 'angle up' : 'angle down'}
                          title={t('setting.internal_billing.usage.details')}
                          aria-label={t(
                            'setting.internal_billing.usage.details'
                          )}
                          onClick={() =>
                            setExpanded((current) => ({
                              ...current,
                              [key]: !open,
                            }))
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <strong>{row.provider_name}</strong>
                        <div className='internal-provider-meta-v817'>
                          {'#' + row.channel_id + ' · ' + row.channel_group}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          basic
                          color={
                            row.provider_billing_class === 'flex'
                              ? 'blue'
                              : row.provider_billing_class === 'batch'
                              ? 'teal'
                              : row.provider_billing_class === 'standard'
                              ? 'green'
                              : row.provider_billing_class === 'express'
                              ? 'orange'
                              : 'red'
                          }
                        >
                          {t(
                            'setting.internal_billing.classes.' +
                              row.provider_billing_class
                          )}
                        </Label>
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatCount(row.request_count)}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatCount(row.usage?.input_units)}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatCount(row.usage?.cached_input_tokens)}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatCount(row.usage?.output_units)}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatProviderCost(
                          row.provider_original_cost_usd,
                          row.provider_cost_available,
                          row.provider_cost_complete
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {formatProviderCost(
                          row.provider_actual_cost_usd,
                          row.provider_cost_available,
                          row.provider_cost_complete
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign='right'>
                        {row.effective_cost_percent == null
                          ? '-'
                          : row.effective_cost_percent + '%'}
                      </Table.Cell>
                    </Table.Row>
                    {open ? (
                      <Table.Row>
                        <Table.Cell colSpan='10'>
                          <div className='internal-meter-grid-v817'>
                            {usageMeterKeysV817.map((meter) => (
                              <div key={meter}>
                                <span>
                                  {t(
                                    'setting.internal_billing.meters.' + meter
                                  )}
                                </span>
                                <strong>
                                  {formatCount(row.usage?.[meter])}
                                </strong>
                              </div>
                            ))}
                          </div>
                          <div className='internal-cost-audit-v817'>
                            <span>
                              {t(
                                'setting.internal_billing.usage.known_attempts'
                              )}
                              : {formatCount(row.known_cost_attempt_count)}
                            </span>
                            <span>
                              {t(
                                'setting.internal_billing.usage.unknown_attempts'
                              )}
                              : {formatCount(row.unknown_cost_attempt_count)}
                            </span>
                            <span>
                              {t(
                                'setting.internal_billing.usage.estimated_attempts'
                              )}
                              : {formatCount(row.estimated_usage_attempt_count)}
                            </span>
                            <span>
                              {t(
                                'setting.internal_billing.usage.missing_attempts'
                              )}
                              : {formatCount(row.missing_usage_attempt_count)}
                            </span>
                            <span>
                              {t(
                                'setting.internal_billing.usage.policy_version'
                              )}
                              : {row.latest_cost_policy_version_id}
                            </span>
                          </div>
                          {row.latest_cost_unresolved_reason ? (
                            <Message
                              warning
                              size='small'
                              content={row.latest_cost_unresolved_reason}
                            />
                          ) : null}
                        </Table.Cell>
                      </Table.Row>
                    ) : null}
                  </Fragment>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
    </section>
  );
};

const InternalBillingSettingV817 = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('default_price');
  const [refreshKey, setRefreshKey] = useState(0);
  const views = [
    ['default_price', 'dollar sign'],
    ['policy', 'sliders horizontal'],
    ['usage', 'table'],
    ['profitability', 'chart line'],
    ['import', 'upload'],
  ];

  return (
    <div className='internal-billing-v817'>
      <Menu secondary pointing>
        {views.map(([key, icon]) => (
          <Menu.Item
            key={key}
            active={activeView === key}
            onClick={() => setActiveView(key)}
          >
            <Icon name={icon} />
            {t('setting.internal_billing.navigation.' + key)}
          </Menu.Item>
        ))}
      </Menu>
      <Segment basic className='internal-billing-content-v817'>
        {activeView === 'default_price' ? <DefaultCustomerPriceV817 /> : null}
        {activeView === 'policy' ? (
          <CostPolicyV817
            onSaved={() => setRefreshKey((current) => current + 1)}
          />
        ) : null}
        {activeView === 'usage' ? (
          <ProviderUsageV817 refreshKey={refreshKey} />
        ) : null}
        {activeView === 'profitability' ? <BillingProfitabilityV82 /> : null}
        {activeView === 'import' ? <BillingPriceImportV82 /> : null}
      </Segment>
    </div>
  );
};

export default InternalBillingSettingV817;
